import { useContext, useEffect, useState } from "react";
import BannerCard from "./banner-shelf/banner-card";
import { TeamsFxContext } from "../app-context";
import Todos from "./todos-shelf/to-dos";
import APIHelper from "../../services/api-helper";
import { APIInterceptor } from "../../core/interceptor/api-interceptor";
import { StateContext } from "../../core/store/state-context";
import { IPromotionProps } from "../../hooks/types";
import Recommended from "./recommended-shelf/recommended-card";
import WelcomeCard from "./welcome-shelf/welcome-card";
import { appSupportedConstants, errorLoggingConstants } from "../../utility/app-constants";
import { GetUnSupportedBrowser, LogError } from "../../utility/app-utilities";
import Cookies from "js-cookie";
import ErrorScreen from "./error-screens/error-screen";

let updatedStates: any = "";
/**
 * Main home component which contains all the components of banner, to-dos
 * @returns 
 */
export default function Home() {
  const { themeString } = useContext(TeamsFxContext);
  const [{ context }] = useContext(StateContext);
  const [axios] = APIInterceptor("Home tab");
  const apiHelper = new APIHelper(axios, context);
  const [promotionsData, setPromotionsData] = useState<IPromotionProps[]>([]);
  const [recommendedData, setRecommendedData] = useState<IPromotionProps[]>([]);
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false);
  const [isRecommendedVisible, setIsRecommendedVisible] = useState(false);
  const [isAppLevelAPIError, setAppLevelAPIError] = useState<boolean>();
  const [isPromotionAPIError, setIsPromotionAPIError] = useState<boolean>();
  const [errorTitle, setErrorTitle] = useState<string>();
  const [errorSubTitle, setErrorSubTitle] = useState<string>();
  const [errorCode, setErrorCode] = useState<string>();
  const [showRefreshButton, setShowRefreshButton] = useState<boolean>();
  const getUnSupportedBrowser = GetUnSupportedBrowser();
  const [state] = useContext(StateContext);
  updatedStates = state;

  /**
   * Get API response for Promotions and Banner
   */
  useEffect(() => {
    if (getUnSupportedBrowser) {
      setErrorValues(appSupportedConstants.unsupportedBrowserTitle, appSupportedConstants.unsupportedBrowserSubTitle, "", false);
    } else {
      apiHelper
        .getPromotions()
        .then(async (response) => {
          const bannerData = response.data.promotions.filter((promotions: { type: string; }) => promotions.type.toLowerCase() === "banner");
          const recommendedData = response.data.promotions.filter((promotions: { type: string; }) => promotions.type.toLowerCase() === "promotion");

          if (bannerData.length > 0) {
            const isBannerDismissed = Cookies.get(bannerData[0].promotionTitle);
            setIsBannerVisible(isBannerDismissed === "true" ? false : true);
            setPromotionsData(bannerData);
          }
          if (recommendedData.length > 0 && recommendedData[0].hasOwnProperty("promotionTitle")) {
            let uniqueLocStorageRecommendations: string[] = JSON.parse(localStorage.getItem("recommendationTitles")!);
            let recommendationUniqueTitles: string[] = [];
            recommendedData.forEach((key: IPromotionProps) => {
              recommendationUniqueTitles.push(key.promotionTitle);
              Object.assign(key, { "counterBadge": true });
              if (uniqueLocStorageRecommendations !== null && uniqueLocStorageRecommendations !== undefined && uniqueLocStorageRecommendations.includes(key.promotionTitle))
                Object.assign(key, { "counterBadge": false });
            });
            localStorage.setItem("recommendationTitles", JSON.stringify(recommendationUniqueTitles));
          }
          setRecommendedData(recommendedData);
          setIsRecommendedVisible(true);
        })
        .catch((error) => {
          if (updatedStates.tokenAPIStatus !== appSupportedConstants.apiSucessStatus && updatedStates.errorMessage !== "") {
            if (updatedStates.errorMessage?.message.includes(appSupportedConstants.timeoutErrorText)) {
              setAppLevelAPIError(true);
              setErrorValues(appSupportedConstants.timeoutErrorTitle, appSupportedConstants.timeoutErrorSubTitle, appSupportedConstants.timeoutErrorStatus, true);
            }
            else if (updatedStates.errorMessage?.message.includes(appSupportedConstants.networkErrorText)) {
              setAppLevelAPIError(true);
              setErrorValues(appSupportedConstants.networkErrorTitle, appSupportedConstants.refreshPageTextSubTitle, appSupportedConstants.networkErrorStatus, true);
            }
            else if (appSupportedConstants.appLevelErrorCodes.includes(updatedStates.errorMessage?.response.status)) {
              setAppLevelAPIError(true);
              setErrorValues(appSupportedConstants.customErrorMessageTitle, appSupportedConstants.refreshPageTextSubTitle, updatedStates.errorMessage?.response.status, true);
            }
            else {
              setAppLevelAPIError(true);
              setErrorValues(appSupportedConstants.somethingWentWrong, appSupportedConstants.refreshPageTextSubTitle, updatedStates.errorMessage?.response.status, true);
            }
          }
          else {
            setIsRecommendedVisible(true);
            setIsPromotionAPIError(true);
            const errorObject = {
              component: errorLoggingConstants.promotionsComponentFile,
              method: errorLoggingConstants.useEffect,
              message: error as string,
              operation: errorLoggingConstants.promotionsAPI,
              params: {}
            };
            LogError(errorObject);
          }
        });
    }
  }, []);

  /**
   * set the error message title, subtitle, errorcode for error screens
   * @param title 
   * @param subTitle 
   * @param errorCode 
   * @param showRefreshButton 
   */
  const setErrorValues = (title: string, subTitle: string, errorCode: string, showRefreshButton: boolean) => {
    setErrorTitle(title);
    setErrorSubTitle(subTitle);
    setErrorCode(errorCode);
    setShowRefreshButton(showRefreshButton);
  }

  return (
    <div className={themeString === "default" ? "light" : themeString === "dark" ? "dark" : "contrast"} data-testid="app-component"   >
      <div className={getUnSupportedBrowser || isAppLevelAPIError ? "main-container error-screen" : "main-container"}>
        {
          !getUnSupportedBrowser && !isAppLevelAPIError ?
            <>
              {isBannerVisible &&
                <BannerCard
                  promotionTitle={promotionsData[0].promotionTitle}
                  promotionBody={promotionsData[0].promotionBody}
                  callToAction={promotionsData[0].callToAction}
                  altText={promotionsData[0].altText}
                  imageURL={promotionsData[0].imageURL}
                  linkHref={promotionsData[0].linkHref}
                  setIsBannerVisible={setIsBannerVisible}
                  counterBadge={false}
                />
              }
              <WelcomeCard />
              <Recommended isRecommendedVisible={isRecommendedVisible} data={recommendedData} isPromotionAPIError={isPromotionAPIError!} />
              <Todos />
            </>
            : <ErrorScreen
              title={errorTitle as string}
              subTitle={errorSubTitle as string}
              errorCode={errorCode as string}
              refreshButton={showRefreshButton as boolean}
            />
        }
      </div>
    </div>
  );
}
